import { InputText, InputTextProps } from 'primereact/inputtext';
import { Column } from '../container/Container';
import { TextBodyExtraExtraSmall } from '../text/Texts';
import { cn } from '../ui/cn';
import { ChangeEvent, JSX, useEffect, useState } from 'react';
import { FormElementProps } from './InputController';
import { MdError } from 'react-icons/md';
import { inputStyle, labelStyle } from './styles';

export interface TextInputProps extends Omit<InputTextProps, 'value' | 'onChange' | 'onBlur'>, FormElementProps {
	label?: string;
	placeholder?: string;
	labelBgColor?: string;
	containerCls?: string;
	icon?: JSX.Element;
	iconCls?: string;
	needErrorIcon?: boolean;
	isFocused?: boolean;
}

const TextInput = ({
	id,
	value,
	onChange,
	onBlur,
	error,

	label,
	placeholder,
	labelBgColor,
	containerCls,
	variant = 'filled',
	icon,
	iconCls,
	needErrorIcon,
	isFocused,
	...textInputProps
}: TextInputProps) => {
	const [isFocus, setIsFocus] = useState(isFocused ?? false);

	const hasValue = value !== null && value !== undefined && value !== '';

	useEffect(() => {
		if (isFocused !== undefined) setIsFocus(isFocused);
	}, [isFocused]);

	return (
		<Column className={cn('w-full relative', containerCls, textInputProps.disabled && 'opacity-50')}>
			<InputText
				unstyled
				{...textInputProps}
				id={id}
				value={value}
				onChange={(e: ChangeEvent<HTMLInputElement>) => onChange?.(e.target.value)}
				onFocus={() => {
					if (textInputProps.readOnly) return;
					setIsFocus(true);
				}}
				onBlur={() => {
					if (isFocused === undefined) setIsFocus(false);
					onBlur?.();
				}}
				variant={undefined}
				pt={{ root: { className: inputStyle({ variant, isFocus, error: !!error }) } }}
			/>

			{/* Placeholder */}
			{(variant === 'filled' && placeholder && !hasValue) && (
				<span
					children={placeholder}
					className={cn(
						'absolute left-4 right-4 top-6 truncate',
						'transition-opacity duration-300 pointer-events-none',
						'font-poppins text-body-large text-onSurfaceVariant',
						'opacity-0', isFocus && 'opacity-100'
					)}
				/>
			)}

			{/* Label */}
			{label && (
				<label
					htmlFor={id}
					children={label}
					className={labelStyle({ variant, isFocus, hasValue, labelBgColor, error: !!error })}
				/>
			)}

			{/* Error */}
			{(error && error.message) && (
				<TextBodyExtraExtraSmall children={error.message} className="pl-4 pt-1 text-error" />
			)}
			{needErrorIcon && error && (
				<MdError size={24} className="absolute top-4 right-4 z-10 text-error" />
			)}

			{/* Icon */}
			{icon && (
				<div className={cn("absolute top-4 right-4 z-10", iconCls)}>
					{icon}
				</div>
			)}
		</Column>
	);
};

export default TextInput;
